import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/utils/i18n';
import { TrendingUp, LogOut, Newspaper, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import MobileNav from '@/components/MobileNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function News() {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [news, setNews] = useState({ all: [], stocks: [], crypto: [] });
  const [stockPicks, setStockPicks] = useState([]);
  const [cryptoPicks, setCryptoPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [language]); // Refetch when language changes

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchNews(), fetchStockPicks(), fetchCryptoPicks()]);
    setLoading(false);
    setLastUpdate(new Date());
  };

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news?lang=${language}`);
      setNews(response.data);
    } catch (error) {
      console.error('Failed to fetch news:', error);
    }
  };

  const fetchStockPicks = async () => {
    try {
      const response = await axios.get(`${API}/recommendations/stocks`);
      setStockPicks(response.data.recommendations);
    } catch (error) {
      console.error('Failed to fetch stock picks:', error);
    }
  };

  const fetchCryptoPicks = async () => {
    try {
      const response = await axios.get(`${API}/recommendations/crypto`);
      setCryptoPicks(response.data.recommendations);
    } catch (error) {
      console.error('Failed to fetch crypto picks:', error);
    }
  };

  const getTimeAgo = (datetime) => {
    const now = new Date();
    const then = new Date(datetime);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const getConsensusBadge = (consensus) => {
    const colors = {
      'STRONG_BUY': 'bg-green-500',
      'BUY': 'bg-green-600',
      'HOLD': 'bg-yellow-500',
      'SELL': 'bg-red-600',
      'STRONG_SELL': 'bg-red-500'
    };
    return colors[consensus] || 'bg-gray-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a0a0a] to-[#0a0a0a]">
      <MobileNav currentPage="news" />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Live News & Research</h1>
            <p className="text-gray-400">Real-time market news and analyst recommendations</p>
          </div>
          <div className="flex items-center gap-4">
            {lastUpdate && <span className="text-sm text-gray-500">Updated {getTimeAgo(lastUpdate)}</span>}
            <Button onClick={fetchAllData} disabled={loading} variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="glass rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <Newspaper className="w-6 h-6 text-orange-500" />
                Latest News
              </h2>

              <Tabs defaultValue="all" className="w-full">
                <TabsList className="grid w-full grid-cols-3 bg-zinc-900 mb-6">
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="stocks">Stocks</TabsTrigger>
                  <TabsTrigger value="crypto">Crypto</TabsTrigger>
                </TabsList>
                
                <TabsContent value="all" className="space-y-4">
                  {news.all.map((item) => (
                    <a key={item.id} href={item.url} target="_blank" rel="noopener noreferrer" className="block p-4 bg-zinc-900/50 rounded-xl hover:bg-zinc-900 transition-all group">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${item.category === 'CRYPTO' ? 'bg-orange-500' : 'bg-blue-500'} text-white`}>{item.category}</span>
                            <span className="text-xs text-gray-500">{item.source}</span>
                            <span className="text-xs text-gray-500">{getTimeAgo(item.datetime)}</span>
                          </div>
                          <h3 className="font-bold text-white mb-2 group-hover:text-orange-400 transition-colors">{item.title}</h3>
                          <p className="text-sm text-gray-400 line-clamp-2">{item.summary}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-orange-500 flex-shrink-0" />
                      </div>
                    </a>
                  ))}
                </TabsContent>

                <TabsContent value="stocks" className="space-y-4">
                  {news.stocks.map((item) => (
                    <a key={item.id} href={item.url} target="_blank" rel="noopener noreferrer" className="block p-4 bg-zinc-900/50 rounded-xl hover:bg-zinc-900 transition-all group">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-gray-500">{item.source}</span>
                            <span className="text-xs text-gray-500">{getTimeAgo(item.datetime)}</span>
                          </div>
                          <h3 className="font-bold text-white mb-2 group-hover:text-orange-400 transition-colors">{item.title}</h3>
                          <p className="text-sm text-gray-400 line-clamp-2">{item.summary}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-orange-500 flex-shrink-0" />
                      </div>
                    </a>
                  ))}
                </TabsContent>

                <TabsContent value="crypto" className="space-y-4">
                  {news.crypto.length > 0 ? news.crypto.map((item) => (
                    <a key={item.id} href={item.url} target="_blank" rel="noopener noreferrer" className="block p-4 bg-zinc-900/50 rounded-xl hover:bg-zinc-900 transition-all group">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-gray-500">{item.source}</span>
                            <span className="text-xs text-gray-500">{getTimeAgo(item.datetime)}</span>
                          </div>
                          <h3 className="font-bold text-white mb-2 group-hover:text-orange-400 transition-colors">{item.title}</h3>
                        </div>
                        <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-orange-500 flex-shrink-0" />
                      </div>
                    </a>
                  )) : <p className="text-gray-400 text-center py-8">No crypto news available</p>}
                </TabsContent>
              </Tabs>
            </div>
          </div>

          <div className="space-y-6">
            <div className="glass rounded-2xl p-6">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                Top Stock Picks
              </h2>
              <div className="space-y-3">
                {stockPicks.slice(0, 5).map((pick, idx) => (
                  <div key={idx} className="p-3 bg-zinc-900/50 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-bold text-white">{pick.symbol}</span>
                      <span className={`px-2 py-1 rounded text-xs font-bold ${getConsensusBadge(pick.consensus)} text-white`}>{pick.consensus.replace('_', ' ')}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">${pick.current_price?.toFixed(2)}</span>
                      <span className={pick.change_percent >= 0 ? 'text-green-400' : 'text-red-400'}>{pick.change_percent >= 0 ? '+' : ''}{pick.change_percent?.toFixed(2)}%</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">{pick.buy + pick.strong_buy} Buy • {pick.hold} Hold • {pick.sell + pick.strong_sell} Sell</div>
                  </div>
                ))}
              </div>
            </div>

            {cryptoPicks.length > 0 && (
              <div className="glass rounded-2xl p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                  Trending Crypto
                </h2>
                <div className="space-y-3">
                  {cryptoPicks.slice(0, 5).map((pick, idx) => (
                    <a key={idx} href={pick.url} target="_blank" rel="noopener noreferrer" className="block p-3 bg-zinc-900/50 rounded-lg hover:bg-zinc-900 transition-colors">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-bold text-white">{pick.symbol}</div>
                          <div className="text-xs text-gray-400">{pick.title}</div>
                        </div>
                        {pick.votes > 0 && <span className="text-green-400 font-bold text-sm">+{pick.votes}</span>}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default News;
